import { useEffect, useRef } from 'react';
import { staticDataUpdatedPreset, readAllFixturesNotice } from '@aelive/dds-client';

import { dds } from '../dds/ddsClient';
import { getFixtureFlags } from '../services/allFixturesLive.service';

/**
 * Keep this client's Live/NLE switches in step with whoever else is editing the
 * fixture (OPUS-1521).
 *
 * WHY THIS EXISTS. all_fixtures.is_live and .is_nle are one shared row, and the
 * only time Drive read them was on mount. So an operator changing Live in Aether
 * — or another Drive changing it — left this screen showing the opposite of what
 * the server held, until someone reloaded.
 *
 * WHY staticDataUpdated AND NOT seasonFixtureResultsUpdated. The footer toggles
 * live in AppRoutes, whose useParams gives { orgId, fixtureId, rendererId } and
 * NO season_id — and seasonFixtureResultsUpdated declares `season_id: String!`.
 * staticDataUpdated is fixture-scoped, and DWS's buildFixtureNotice puts
 * fixture_id in `key` precisely because DDS's resolver filters on it.
 *
 * WHY IT RE-READS INSTEAD OF USING THE PAYLOAD. A notice is a change
 * DESCRIPTOR: DWS sets `patched: null` and carries no field values at all, on
 * purpose. The notice says WHAT changed; the row says what it became.
 *
 * DELIVERY IS GATED. An is_live/is_nle-only notice reaches subscribers only
 * where DDS_ALL_FIXTURES_SOURCE is on (it is on for dev and test; check before
 * assuming it on PLS). Where it is off this hook is simply quiet — the
 * read-on-open effect in AppRoutes remains the floor, so nothing breaks, it just
 * stops being live.
 */

/** Matches the season-notice debounce in useFixtureData and Aether's useAllFixturesLive. */
export const FLAG_SYNC_DEBOUNCE_MS = 750;

/**
 * Local rather than imported from @aelive/dds-client: the package gained
 * noticeTouchesNleFlag in 0.8.9, and a dependency bump must not gate this hook.
 * Swap to the import once both apps are on it.
 */
const FLAG_FIELDS = ['is_live', 'is_nle'];

function touchesFlags(notice) {
  const fields = notice?.changed_fields;
  if (!Array.isArray(fields)) return false;
  // An EMPTY list is a create, a bulk, or a revision-only bump: we cannot tell
  // what moved, so we re-read. That is the same rule DDS applies to itself.
  if (fields.length === 0) return true;
  return fields.some((f) => FLAG_FIELDS.includes(f));
}

/**
 * Who made the change.
 *
 * Read from the normalised notice when the installed @aelive/dds-client is new
 * enough to carry it, and from the raw payload otherwise. DO NOT "tidy" this to
 * the notice alone until both apps are on 0.8.9+ — readAllFixturesNotice
 * whitelists fields, so on an older client the actor is silently dropped and the
 * toast quietly loses its name.
 */
function readActor(notice, payload) {
  const data = payload?.data || {};
  return {
    actorId: notice?.actor_id ?? data.actor_id ?? null,
    actorName: notice?.actor_name ?? data.actor_name ?? null,
  };
}

/**
 * @param {object} params
 * @param {string|null} params.fixtureId
 * @param {(flags: {isLive: boolean, isNle: boolean},
 *          meta: {self: boolean, actorName: string|null}) => void} params.onFlagsChanged
 * @param {string|null} [params.selfActorId]  suppress the toast for our own writes
 * @param {() => number} [params.getSuppressUntil]  epoch ms; blunt fallback for
 *   sessions with no userId to compare against
 */
export function useFixtureFlagSync({
  fixtureId,
  onFlagsChanged,
  selfActorId = null,
  getSuppressUntil,
}) {
  // Held in refs so a new callback identity — which happens on every render of
  // the component that owns the toggles — cannot tear down and rebuild the
  // subscription underneath a live match.
  const onChangedRef = useRef(onFlagsChanged);
  const selfIdRef = useRef(selfActorId);
  const suppressRef = useRef(getSuppressUntil);
  useEffect(() => { onChangedRef.current = onFlagsChanged; }, [onFlagsChanged]);
  useEffect(() => { selfIdRef.current = selfActorId; }, [selfActorId]);
  useEffect(() => { suppressRef.current = getSuppressUntil; }, [getSuppressUntil]);

  useEffect(() => {
    if (!fixtureId) return undefined;

    let disposed = false;
    let timer = null;
    let latestActor = { actorId: null, actorName: null };

    const reconcile = async () => {
      timer = null;
      const actor = latestActor;
      const res = await getFixtureFlags(fixtureId);
      if (disposed || !res.ok) return;

      // A self-made change still reconciles — it is a no-op when the state
      // already matches, and a repair when our own write partly failed. Only the
      // TOAST is suppressed.
      const bySelf =
        (selfIdRef.current && actor.actorId && actor.actorId === selfIdRef.current)
        || Date.now() < (suppressRef.current?.() ?? 0);

      onChangedRef.current?.(
        { isLive: res.isLive, isNle: res.isNle },
        { self: Boolean(bySelf), actorName: actor.actorName },
      );
    };

    let unsubscribe;
    try {
      unsubscribe = dds.subscribe(
        staticDataUpdatedPreset({ fixture_id: fixtureId }),
        (msg) => {
          const payload = msg?.staticDataUpdated;
          const notice = readAllFixturesNotice(payload);
          if (!notice) return;
          // `notice.fixture_id &&` is deliberate: a BULK notice carries a null
          // fixture_id and must still be honoured.
          if (notice.fixture_id && notice.fixture_id !== fixtureId) return;
          if (!touchesFlags(notice)) return;

          // Last writer in the burst names the toast; the re-read is
          // authoritative for the values either way.
          latestActor = readActor(notice, payload);
          if (timer) return;
          timer = setTimeout(reconcile, FLAG_SYNC_DEBOUNCE_MS);
        },
      );
    } catch (err) {
      console.warn('[fixtureFlagSync] could not subscribe:', err?.message || err);
      return undefined;
    }

    return () => {
      disposed = true;
      if (timer) clearTimeout(timer);
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [fixtureId]);
}

export default useFixtureFlagSync;
